import React from 'react';
import SearchFilters from './components/SearchFilters';
import ClinicCard from './components/ClinicCard';
import Pagination from './components/Pagination';
import NetworkBackground from './components/NetworkBackground';
import useClinicSearch from './hooks/useClinicSearch';

export default function App({ apiBase }) {
  const {
    // Data
    clinics,
    regions,
    provinces,
    products,
    // Filter state
    searchTerm,
    selectedRegion,
    selectedProvince,
    selectedProducts,
    // Pagination
    currentPage,
    totalPages,
    totalClinics,
    perPage,
    // Loading / error
    loading,
    error,
    // Setters
    setSearchTerm,
    setSelectedRegion,
    setSelectedProvince,
    setSelectedProducts,
    setCurrentPage,
    clearFilters,
    // Geolocation
    nearMeActive,
    geoLoading,
    geoError,
    requestNearMe,
    // Settings
    heroBgUrl,
  } = useClinicSearch(apiBase);

  const hasActiveFilters =
    searchTerm !== '' ||
    selectedRegion !== '' ||
    selectedProvince !== '' ||
    selectedProducts.length > 0;

  return (
    <div className="cs-app">
      {/* ── Page Header / Hero ──────────────────────────────────── */}
      {/* Architecture (v1.0.3): Hero is rendered as a sibling of .cs-content
          so it inherits the full width of #clinic-search-app's parent
          (<main id="main">) — no CSS escape hacks required. The theme's
          .nk-container wrapper has been removed from archive-clinic.php,
          allowing #clinic-search-app to stretch edge-to-edge.

          When admin sets a hero background image (Clinic Search → Settings),
          we apply it as a CSS background-image on .cs-hero and add the
          .cs-hero--bg-image modifier so the styles render a dark overlay for
          text contrast. Otherwise the .cs-hero base styles fall back to the
          original teal/cyan gradient. */}
      <section
        className={`cs-hero${heroBgUrl ? ' cs-hero--bg-image' : ''}`}
        style={heroBgUrl ? { backgroundImage: `url(${heroBgUrl})` } : undefined}
      >
        {/* Animated particle network — only render when no admin BG image
            is set (image takes precedence over the canvas backdrop). */}
        {!heroBgUrl && <NetworkBackground />}
        <div className="cs-hero__inner">
          <h1 className="cs-hero__title">ค้นหาคลินิกทั่วประเทศไทย</h1>
          <p className="cs-hero__subtitle">
            พบคลินิกผู้จำหน่ายสินค้าของเราใกล้คุณ
          </p>
        </div>
      </section>

      {/* ── Content wrapper (max-width 1120px, internally centered) ── */}
      {/* Everything below the hero lives inside this wrapper so results bar,
          sidebar+grid layout, and pagination all align to the same readable
          column while the hero remains full-bleed above. */}
      <div className="cs-content">
        {/* ── Layout ──────────────────────────────────────────────── */}
        <div className="cs-layout">
          {/* Sidebar */}
          {/* v1.0.4: Region dropdown is back in the UI (above Province) and
              drives the province cascade in useClinicSearch — picking a region
              re-fetches /provinces?region_id=X and clears the province pick. */}
          <aside className="cs-sidebar">
            <SearchFilters
              regions={regions}
              provinces={provinces}
              products={products}
              searchTerm={searchTerm}
              selectedRegion={selectedRegion}
              selectedProvince={selectedProvince}
              selectedProducts={selectedProducts}
              onSearchChange={setSearchTerm}
              onRegionChange={setSelectedRegion}
              onProvinceChange={setSelectedProvince}
              onProductToggle={(id) => {
                setSelectedProducts((prev) =>
                  prev.includes(id)
                    ? prev.filter((p) => p !== id)
                    : [...prev, id]
                );
              }}
              onClearFilters={clearFilters}
              nearMeActive={nearMeActive}
              geoLoading={geoLoading}
              geoError={geoError}
              onNearMeToggle={requestNearMe}
            />
          </aside>

          {/* Main content */}
          <main className="cs-main">
            {/* Result count */}
            <div className="cs-results-bar">
              {loading ? (
                <span className="cs-results-bar__count">กำลังค้นหา...</span>
              ) : error ? (
                <span className="cs-results-bar__error">{error}</span>
              ) : (
                <span className="cs-results-bar__count">
                  {totalClinics > 0
                    ? `พบ ${totalClinics.toLocaleString('th-TH')} คลินิก`
                    : 'ไม่พบคลินิกที่ตรงกับเงื่อนไข'}
                </span>
              )}

              {hasActiveFilters && !loading && (
                <button className="cs-clear-btn" onClick={clearFilters}>
                  ล้างตัวกรองทั้งหมด
                </button>
              )}
            </div>

            {/* Clinic grid */}
            {loading ? (
              <SkeletonGrid count={perPage} />
            ) : error ? (
              <div className="cs-error-state">
                <p>เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง</p>
                <button className="cs-btn cs-btn--teal" onClick={clearFilters}>
                  รีเซ็ต
                </button>
              </div>
            ) : clinics.length === 0 ? (
              <EmptyState hasFilters={hasActiveFilters} onClear={clearFilters} />
            ) : (
              <div className="cs-grid">
                {clinics.map((clinic) => (
                  <ClinicCard key={clinic.id} clinic={clinic} />
                ))}
              </div>
            )}

            {/* Pagination */}
            {!loading && totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={(page) => {
                  setCurrentPage(page);
                  // Smooth scroll to the results bar so the user lands on the
                  // new page's first card — not back at the hero. Falls back
                  // to the app root if the results bar isn't found yet.
                  const target =
                    document.querySelector('.cs-results-bar') ||
                    document.getElementById('clinic-search-app');
                  if (target) {
                    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  } else {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }
                }}
              />
            )}
          </main>
        </div>
      </div>
    </div>
  );
}

// ── Skeleton loading grid ─────────────────────────────────────────────────────

function SkeletonGrid({ count }) {
  return (
    <div className="cs-grid">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="cs-card cs-card--skeleton">
          <div className="cs-skeleton cs-skeleton--title" />
          <div className="cs-skeleton cs-skeleton--line" />
          <div className="cs-skeleton cs-skeleton--line cs-skeleton--short" />
          <div className="cs-skeleton cs-skeleton--badges" />
        </div>
      ))}
    </div>
  );
}

// ── Empty state ───────────────────────────────────────────────────────────────

function EmptyState({ hasFilters, onClear }) {
  return (
    <div className="cs-empty-state">
      <div className="cs-empty-state__icon">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>
      <h3 className="cs-empty-state__title">ไม่พบคลินิก</h3>
      <p className="cs-empty-state__desc">
        {hasFilters
          ? 'ลองปรับตัวกรองหรือคีย์เวิร์ดการค้นหา'
          : 'ยังไม่มีข้อมูลคลินิกในระบบ'}
      </p>
      {hasFilters && (
        <button className="cs-btn cs-btn--teal" onClick={onClear}>
          ล้างตัวกรอง
        </button>
      )}
    </div>
  );
}
